import React, {FC, useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {batch, ResolveDispatchProps} from '../../../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../helpers/reselectHelpers';
import {FrameTypesEnum} from '../../../../../../models/api/apiTypes';
import {ContactModel} from '../../../../../../models/api/contactModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {VisualRecipientModel} from '../../../../../../models/api/visualRecipientModel';
import {AppState} from '../../../../../../store/appState';
import {makeGetVisualRecipient} from '../../../../../../store/data/contacts/recipientsSelectors';
import {updateSelectedSideFrameIdAction} from '../../../../../../store/data/localPreferences/localPreferencesActions';
import {
  clickRecentCommunicationsBannerAction,
  dismissRecentCommunicationsBannerAction,
  fetchRecentConversationsAsyncAction,
} from '../../../../../../store/metadata/recentCommunications/recentCommunicationsActions';
import {
  makeGetRecentCommunications,
  makeGetShouldFetchRecentConversations,
} from '../../../../../../store/metadata/recentCommunications/recentCommunicationsSelectors';
import {RecentCommunications} from '../../../../../../store/metadata/recentCommunications/recentCommunicationsState';
import {RecentCommunicationsBanner} from './recentCommunicationsBanner';

/*
 * Props.
 */

interface RecentCommunicationsBannerContainerPublicProps {
  conversation: ConversationModel;
  teammateId: number;
  shouldRenderAsComposerBanner: boolean;
  /** Callback to run after the data is fetched and the banner is not displayed */
  onEmptyRenderAfterDataFetch?: Handler;
  /** Callback to run when the banner is clicked, either to view or dismiss */
  onClick?: Handler;
}

type RecentCommunicationsBannerContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type RecentCommunicationsBannerContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type RecentCommunicationsBannerContainerProps = RecentCommunicationsBannerContainerPublicProps &
  RecentCommunicationsBannerContainerStateProps &
  RecentCommunicationsBannerContainerDispatchProps;

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: RecentCommunicationsBannerContainerPublicProps) {
  return props.conversation;
}

function getTeammateId(_state: AppState, props: RecentCommunicationsBannerContainerPublicProps) {
  return props.teammateId;
}

function getContactUid(_state: AppState, props: RecentCommunicationsBannerContainerPublicProps) {
  return props.conversation.recipient?.contactUid;
}

/*
 * Component.
 */

const RecentCommunicationsBannerContainerComponent: FC<RecentCommunicationsBannerContainerProps> = (
  props,
) => {
  const {
    clickBanner,
    conversation,
    dismissBanner,
    fetchRecentConversations,
    onClick,
    onEmptyRenderAfterDataFetch,
    selectSideFrame,
    shouldFetchRecentConversations,
    shouldRenderAsComposerBanner,
    recentCommunications,
    visualRecipient,
  } = props;
  const shouldRenderBanner = computeShouldRenderBanner(visualRecipient, recentCommunications, conversation);
  const [fetchStatus, setFetchStatus] = useState<AsyncStatusesEnum | undefined>(undefined);

  useEffect(() => {
    const makeFetchRequest = async (contact: ContactModel) => {
      setFetchStatus(AsyncStatusesEnum.LOADING);
      try {
        await fetchRecentConversations(contact);
        setFetchStatus(AsyncStatusesEnum.LOADED);
      } catch {
        setFetchStatus(AsyncStatusesEnum.FAILED);
      }
    };

    if (shouldFetchRecentConversations && visualRecipient && !fetchStatus) {
      makeFetchRequest(visualRecipient.contact);
    }
  }, [fetchRecentConversations, shouldFetchRecentConversations, visualRecipient, fetchStatus]);

  useEffect(() => {
    if (recentCommunications && onEmptyRenderAfterDataFetch && !shouldRenderBanner) {
      onEmptyRenderAfterDataFetch();
    }
  }, [onEmptyRenderAfterDataFetch, recentCommunications, shouldRenderBanner]);

  const onClose = useCallback(() => {
    if (visualRecipient) {
      dismissBanner(visualRecipient.contact);
    }

    if (onClick) {
      onClick();
    }
  }, [dismissBanner, onClick, visualRecipient]);

  const onView = useCallback(() => {
    if (visualRecipient) {
      batch(() => {
        clickBanner(visualRecipient.contact);
        selectSideFrame(FrameTypesEnum.CUSTOMER_VIEW);
      });
    }

    if (onClick) {
      onClick();
    }
  }, [clickBanner, onClick, selectSideFrame, visualRecipient]);

  if (
    !shouldRenderBanner ||
    !visualRecipient ||
    !recentCommunications ||
    fetchStatus !== AsyncStatusesEnum.LOADED
  ) {
    return null;
  }

  return (
    <RecentCommunicationsBanner
      onClose={onClose}
      onView={onView}
      visualRecipient={visualRecipient}
      recentCommunicationsBannerStatus={recentCommunications.bannerStatus}
      recentCommunicationsCount={calculateRecentCommunicationsCount(recentCommunications, conversation.id)}
      shouldRenderAsComposerBanner={shouldRenderAsComposerBanner}
    />
  );
};

/*
 * Helpers.
 */

const computeShouldRenderBanner = (
  visualRecipient: VisualRecipientModel | undefined,
  recentCommunications: RecentCommunications | undefined,
  conversation: ConversationModel,
) => {
  if (!visualRecipient || !recentCommunications) {
    return false;
  }

  // If all of the recent conversations are from a private namespace, then we don't want to show the banner
  // because our assumption is that the user already has awareness of conversations in their personal inbox
  if (recentCommunications.isOnlyPrivate) {
    return false;
  }

  const recentCommunicationsCount = calculateRecentCommunicationsCount(recentCommunications, conversation.id);
  if (recentCommunicationsCount < 1) {
    return false;
  }

  return true;
};

const calculateRecentCommunicationsCount = (
  recentCommunications: RecentCommunications,
  conversationId: number,
) => (recentCommunications.conversationIds ?? []).filter((id) => id !== conversationId).length;

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<RecentCommunicationsBannerContainerPublicProps>()(() => {
  const getVisualRecipient = makeGetVisualRecipient(
    getConversation,
    getTeammateId,
    'recentCommunicationsBannerContainer',
  );
  const getRecentCommunications = makeGetRecentCommunications(getContactUid);
  return {
    shouldFetchRecentConversations: makeGetShouldFetchRecentConversations(
      getVisualRecipient,
      getRecentCommunications,
    ),
    recentCommunications: getRecentCommunications,
    visualRecipient: getVisualRecipient,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  dismissBanner: dismissRecentCommunicationsBannerAction,
  fetchRecentConversations: fetchRecentConversationsAsyncAction,
  clickBanner: clickRecentCommunicationsBannerAction,
  selectSideFrame: updateSelectedSideFrameIdAction,
}));

export const RecentCommunicationsBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(RecentCommunicationsBannerContainerComponent);
